// @ts-nocheck

import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useYieldCallback } from '@zillow/yield-callback';
import constants from 'app/shared/constants/ConstantsBundle';
import controller from './controller';
import IconArrowLeftDark from 'images/icons/arrow-left-dark-grey.svg';
import IconArrowRightDark from 'images/icons/arrow-right-dark-grey.svg';
import { noop } from 'lodash';
import {
  photoCarouselV2Arrow,
  PhotoCarouselV2ArrowControls,
  photoCarouselV2ArrowDisabled,
  photoCarouselV2ArrowRight,
  PhotoCarouselV2Container,
  photoCarouselV2Item,
  photoCarouselV2ItemCount,
  PhotoCarouselV2ItemCountBadge,
  photoCarouselV2ItemCountBottomRight,
  photoCarouselV2ItemCountHide,
  photoCarouselV2ItemCountTopRight,
  photoCarouselV2ItemHidden,
  PhotoCarouselV2ItemWrapper,
  PhotoCarouselV2NextTarget,
  PhotoCarouselV2PrevTarget,
  photoCarouselV2Track,
  photoCarouselV2TrackTransition,
  PhotoCarouselV2 as PhotoCarouselV2Wrapper,
} from './PhotoCarouselV2.styles';

interface PhotoCarouselV2Props {
  animateChange?: boolean;
  backgroundColor?: string;
  children: React.ReactNode;
  className?: string;
  defaultIndex?: number;
  ignoreKeypress?: boolean;
  itemCountOffset?: number;
  itemCountPosition?: string;
  loopable?: boolean;
  maxIndex?: number;
  onChange?: (index: number) => void;
  showItemCount?: boolean;
}

const sanitizeIndex = (index, indexToDefaultTo, children) => {
  if (index !== 0 && !index) {
    return indexToDefaultTo;
  }

  const childComponents = React.Children.toArray(children);
  if (index < 0 || index > childComponents.length) {
    return 0;
  }

  return index;
};

// TODO: deconstruct props & implement useCallback.
const PhotoCarouselV2 = ({
  animateChange = true,
  backgroundColor = '#000',
  children,
  className,
  defaultIndex,
  ignoreKeypress = false,
  itemCountOffset = 0,
  itemCountPosition = 'bottom-right',
  loopable = false,
  maxIndex,
  onChange = noop,
  showItemCount = true,
}: PhotoCarouselV2Props) => {
  const containerRef = useRef();
  const startXRef = useRef();
  const startYRef = useRef();
  const endXRef = useRef();
  const prevXRef = useRef();
  const prevYRef = useRef();
  const [useTransition, setUseTransition] = useState(false);
  const [positionX, setPositionX] = useState(false);
  const [isMounting, setIsMounting] = useState(true);
  const [index, setIndex] = useState(sanitizeIndex(defaultIndex, 0, children));
  const [containerWidth, setContainerWidth] = useState(0);
  const childrenArray = React.Children.toArray(children);
  const maxIndexValue = maxIndex ? maxIndex : childrenArray.length - 1;
  const isFirst = index === 0;
  const isLast = index === maxIndex;

  // Calculate container width and handle Safari-specific timing issues
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        // Round to avoid fractional pixel issues in Safari
        setContainerWidth(Math.round(width));
      }
    };

    // Initial calculation
    updateContainerWidth();

    // Safari fix: Use requestAnimationFrame instead of setTimeout for better performance
    const rafId = requestAnimationFrame(updateContainerWidth);

    // Handle resize events
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (isMounting) {
      setIsMounting(false);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [isMounting]);

  let realX = 0;
  if (containerWidth) {
    // With virtual positioning, track transform only handles drag/swipe offset
    // Individual slides are positioned absolutely, so track stays centered
    realX = Math.round(positionX);
  }

  const maxDisplayItemCount = maxIndexValue + 1 + itemCountOffset;
  const currentDisplayIndex = index + 1;
  const displayItemCount = `${currentDisplayIndex}/${maxDisplayItemCount}`;

  // Apply Safari-specific fixes only on client side to avoid hydration issues
  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const userAgent = window.navigator.userAgent;
      const safari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      setIsSafari(safari);
    }
  }, []);

  const itemStyle = {
    width: containerWidth ? controller.getPx(containerWidth) : '100%',
  };
  const trackStyle = {
    transform: `translate3d(${realX}px, 0px, 0px)`,
    // @ts-ignore - webkit prefix for Safari
    WebkitTransform: `translate3d(${realX}px, 0px, 0px)`,
    // Use minimal width (300%) to avoid LCP issues while supporting prev/current/next slides
    // This eliminates the need for massive track widths that can harm performance
    width: containerWidth && !isMounting ? controller.getPx(containerWidth * 3) : '100%',
    // Apply font-size fix only on Safari after mount to avoid hydration issues
    ...(isSafari && !isMounting ? { fontSize: 0 } : {}),
  };

  const goToIndex = useYieldCallback((newIndex, animateChangeParam) => {
    if (newIndex < 0) {
      newIndex = loopable ? maxIndexValue : 0;
    } else if (newIndex > maxIndexValue) {
      newIndex = loopable ? 0 : maxIndexValue;
    }

    setUseTransition(animateChangeParam);
    setPositionX(0);
    setIndex(newIndex);
    onChange(newIndex);
  });

  useEffect(() => {
    const handleKeyboardNav = (e) => {
      if (ignoreKeypress) {
        return;
      }

      if (e.keyCode === constants.LEFT_ARROW_KEYCODE) {
        goToIndex(index - 1, animateChange);
      } else if (e.keyCode === constants.RIGHT_ARROW_KEYCODE) {
        goToIndex(index + 1, animateChange);
      }
    };

    document.addEventListener('keydown', handleKeyboardNav);
    return () => {
      document.removeEventListener('keydown', handleKeyboardNav);
    };
  }, [goToIndex, index, animateChange, ignoreKeypress]);

  const handleSwiping = useCallback(
    (e) => {
      const touchX = controller.touchX(e);
      const touchY = controller.touchY(e);
      const movedX = touchX - startXRef.current;
      const overscrollMultiplier = 0.2;
      let newPositionX;

      const deltaX = prevXRef.current - touchX;
      const deltaY = prevYRef.current - touchY;
      const isVerticalSwipe = Math.abs(deltaY) >= Math.abs(deltaX);
      prevXRef.current = touchX;
      prevYRef.current = touchY;
      if (isVerticalSwipe) {
        return false;
      }

      // no need for e.preventDefault() - https://www.chromestatus.com/features/5093566007214080
      e.stopPropagation();

      if (isFirst) {
        const maxOverscrollRightX = -1 * (containerWidth * (1 + overscrollMultiplier));
        const maxOverscrollLeftX = containerWidth * overscrollMultiplier;
        if (movedX > maxOverscrollLeftX) {
          newPositionX = maxOverscrollLeftX;
        } else if (movedX < maxOverscrollRightX) {
          newPositionX = maxOverscrollRightX;
        } else {
          newPositionX = movedX;
        }
      } else if (isLast) {
        const maxOverscrollRightX = -1 * (containerWidth * overscrollMultiplier);
        const maxOverscrollLeftX = containerWidth * (1 + overscrollMultiplier);
        if (movedX > maxOverscrollLeftX) {
          newPositionX = maxOverscrollLeftX;
        } else if (movedX < maxOverscrollRightX) {
          newPositionX = maxOverscrollRightX;
        } else {
          newPositionX = movedX;
        }
      } else {
        const maxOverscrollX = containerWidth * (1 + overscrollMultiplier);
        const isMaxedOut = Math.abs(movedX) > maxOverscrollX;
        newPositionX = isMaxedOut ? Math.sign(movedX) * maxOverscrollX : movedX;
      }

      setUseTransition(false);
      setPositionX(newPositionX);
    },
    [containerWidth, isFirst, isLast],
  );

  const handleEnd = useCallback(
    (e) => {
      endXRef.current = controller.touchX(e);
      const swipeLimitX = 50;
      const movedX = endXRef.current - startXRef.current;
      const prev = movedX > swipeLimitX;
      const next = movedX < -swipeLimitX;
      if (prev) {
        goToIndex(index - 1, true);
      } else if (next) {
        goToIndex(index + 1, true);
      } else {
        setUseTransition(true);
        setPositionX(0);
      }

      document.removeEventListener('mousemove', handleSwiping);
      document.removeEventListener('touchmove', handleSwiping);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
    },
    [goToIndex, handleSwiping, index],
  );

  useEffect(() => {
    if (defaultIndex) {
      goToIndex(defaultIndex);
    }

    document.removeEventListener('mousemove', handleSwiping);
    document.removeEventListener('touchmove', handleSwiping);
    document.removeEventListener('mouseup', handleEnd);
    document.removeEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleSwiping);
      document.removeEventListener('touchmove', handleSwiping);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
    };
  }, []);

  const handleStart = useCallback(
    (e) => {
      startXRef.current = prevXRef.current = controller.touchX(e);
      startYRef.current = prevYRef.current = controller.touchY(e);
      // Use passive: false for touch events to prevent Safari scrolling issues
      document.addEventListener('mousemove', handleSwiping);
      document.addEventListener('touchmove', handleSwiping, { passive: false });
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchend', handleEnd);
    },
    [handleEnd, handleSwiping],
  );

  const yieldHandleCallback = useYieldCallback((e, onClick) => {
    const movedX = endXRef.current - startXRef.current;
    if (Math.abs(movedX) < 2 && onClick) {
      onClick(e);
    }
  });

  const handleCenterClick = useCallback(
    (e, onClick) => {
      endXRef.current = controller.touchX(e);
      e.stopPropagation();
      e.preventDefault();
      yieldHandleCallback(e, onClick);
    },
    [yieldHandleCallback],
  );

  const handleClickNext = useCallback(
    (e) => {
      e.stopPropagation();
      goToIndex(index + 1, animateChange);
    },
    [goToIndex, index, animateChange],
  );

  const handleClickPrev = useCallback(
    (e) => {
      e.stopPropagation();
      goToIndex(index - 1, animateChange);
    },
    [goToIndex, index, animateChange],
  );

  return (
    <PhotoCarouselV2Wrapper className={className}>
      <PhotoCarouselV2Container
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        ref={containerRef}
        style={{ backgroundColor }}
      >
        <ul
          className={cx(photoCarouselV2Track, {
            [photoCarouselV2TrackTransition]: useTransition,
          })}
          style={trackStyle}
        >
          {childrenArray.map((child, i) => {
            // Only render slides that are adjacent to current slide for performance
            const isVisible = Math.abs(i - index) <= 1;
            if (!isVisible) return null;

            // Calculate position: prev slide (-100%), current (0%), next slide (100%)
            const relativePosition = i - index;
            const slidePosition = containerWidth ? relativePosition * containerWidth : 0;

            const slideItemStyle = {
              ...itemStyle,
              position: 'absolute' as const,
              left: controller.getPx(slidePosition),
              top: 0,
            };

            return (
              <PhotoCarouselV2ItemWrapper
                className={cx({
                  [photoCarouselV2ItemHidden]: index !== i,
                })}
                style={slideItemStyle}
                key={child.key}
              >
                {React.cloneElement(child, {
                  className: cx(photoCarouselV2Item, child.props.className),
                  onClick: (e) => handleCenterClick(e, child.props.onClick),
                  style: { width: '100%' },
                })}
              </PhotoCarouselV2ItemWrapper>
            );
          })}
        </ul>
        {showItemCount && currentDisplayIndex <= maxDisplayItemCount && (
          <div
            className={cx(photoCarouselV2ItemCount, {
              [photoCarouselV2ItemCountBottomRight]: itemCountPosition === 'bottom-right',
              [photoCarouselV2ItemCountTopRight]: itemCountPosition === 'top-right',
              [photoCarouselV2ItemCountHide]: itemCountPosition === 'none',
            })}
          >
            <PhotoCarouselV2ItemCountBadge>{displayItemCount}</PhotoCarouselV2ItemCountBadge>
          </div>
        )}
        {childrenArray.length > 1 && (
          <PhotoCarouselV2ArrowControls>
            <PhotoCarouselV2PrevTarget onClick={handleClickPrev}>
              <button
                aria-label="Previous image"
                className={cx(photoCarouselV2Arrow, {
                  [photoCarouselV2ArrowDisabled]: isFirst,
                })}
                onClick={handleClickPrev}
              >
                <img src={IconArrowLeftDark} width="16px" height="16px" alt="" />
              </button>
            </PhotoCarouselV2PrevTarget>
            <PhotoCarouselV2NextTarget onClick={handleClickNext}>
              <button
                aria-label="Next image"
                className={cx(photoCarouselV2Arrow, photoCarouselV2ArrowRight, {
                  [photoCarouselV2ArrowDisabled]: isLast,
                })}
                onClick={handleClickNext}
              >
                <img src={IconArrowRightDark} width="16px" height="16px" alt="" />
              </button>
            </PhotoCarouselV2NextTarget>
          </PhotoCarouselV2ArrowControls>
        )}
      </PhotoCarouselV2Container>
    </PhotoCarouselV2Wrapper>
  );
};

export default PhotoCarouselV2;
